import { useState, useContext } from "react";
import axios from "../api/axios";
import { AuthContext } from "../auth/AuthContext";
import { useNavigate } from "react-router-dom";

import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    Card,
    CardContent,
    CircularProgress,
} from "@mui/material";

import { useSnackbar } from "../components";

export default function Login() {
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();
    const { showSnackbar } = useSnackbar();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [pageError, setPageError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleLogin = async () => {
        if (loading) return;

        setPageError("");

        if (!email || !password) {
            setPageError("Email and password are required.");
            return;
        }

        try {
            setLoading(true);

            const res = await axios.post("/auth/login", {
                email,
                password,
            });

            login(res.data.token, res.data.user);
            showSnackbar("Signed in successfully.", "success");
            const defaultPathByRole = {
                FIELD_TECH: "/tasks",
                POS_TECHNICIAN: "/pos/tickets",
                INVENTORY_ADMIN: "/dashboard",
                OFFICE_TECH: "/repair-warranty",
            };

            navigate(defaultPathByRole[res.data.user?.role] || "/dashboard");
        } catch (err) {
            setPageError(err.response?.data?.message || "Sign-in failed.");
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                "@supports (height: 100dvh)": {
                    minHeight: "100dvh",
                },
                display: "grid",
                placeItems: "center",
                px: { xs: 1.5, sm: 2 },
                py: { xs: 2, sm: 4 },
                background:
                    "radial-gradient(circle at top, rgba(59,130,246,0.14) 0%, rgba(248,250,252,1) 45%, rgba(241,245,249,1) 100%)",
                overflow: "auto",
            }}
        >
            <Card
                sx={{
                    width: { xs: "100%", sm: 420 },
                    maxWidth: { xs: 420, sm: "100%" },
                    borderRadius: { xs: 3, sm: 4 },
                    border: "1px solid rgba(148,163,184,0.2)",
                    boxShadow: {
                        xs: "0 16px 42px rgba(15,23,42,0.1)",
                        sm: "0 24px 60px rgba(15,23,42,0.12)",
                    },
                    overflow: "hidden",
                }}
            >
                <CardContent
                    sx={{
                        p: { xs: 2.25, sm: 3, md: 4 },
                        "&:last-child": {
                            pb: { xs: 2.25, sm: 3, md: 4 },
                        },
                    }}
                >
                    <Box textAlign="center" mb={{ xs: 2.5, sm: 3.5 }}>
                        <Typography
                            variant="overline"
                            sx={{
                                color: "text.secondary",
                                fontWeight: 800,
                                letterSpacing: 1.2,
                            }}
                        >
                            MegaPay TMS
                        </Typography>

                        <Typography
                            variant="h4"
                            sx={{
                                mt: 0.5,
                                fontWeight: 800,
                                letterSpacing: "-0.03em",
                                fontSize: { xs: "1.85rem", sm: "2.125rem" },
                            }}
                        >
                            Sign in
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                mt: 1,
                                lineHeight: 1.65,
                                maxWidth: 320,
                                mx: "auto",
                            }}
                        >
                            Access inventory, task tracking, approvals, and reporting.
                        </Typography>
                    </Box>

                    {pageError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {pageError}
                        </Alert>
                    )}

                    <Box display="flex" flexDirection="column" gap={2}>
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fullWidth
                            autoComplete="email"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleLogin();
                            }}
                        />

                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            autoComplete="current-password"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") handleLogin();
                            }}
                        />

                        <Button
                            variant="contained"
                            onClick={handleLogin}
                            disabled={loading}
                            sx={{
                                height: { xs: 46, sm: 48 },
                                mt: 0.5,
                                borderRadius: 2.5,
                                fontWeight: 700,
                                textTransform: "none",
                            }}
                        >
                            {loading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                "Sign in"
                            )}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
}
